import React, { Component } from "react";
import Highcharts from 'highcharts';
import { CircularProgress, Button, ButtonGroup } from '@material-ui/core';
import ViewUtils from '../ViewUtils';
import { REPORT_CONFIG } from './index';
import "./DashboardCharts.css";
import { BarChart, ViewWeek } from '@material-ui/icons';

const utils = new ViewUtils();

export const withChartLogic = (WrappedComponent, defaultType) => {
    return class extends Component {
        constructor(props) {
            super(props);
            this.chartRef = React.createRef();
            this.chart = null;
            this.lastResponse = null; 
            this.state = { 
                loading: false, 
                noData: false,
                chartType: defaultType === 'bar' ? 'column' : defaultType
            };
        }

        componentDidMount() { this.checkAndFetch(); }
        
        componentDidUpdate(prevProps, prevState) {
            const { reportName, FinancialYearID, QuarterID } = this.props;
            if (prevProps.reportName !== reportName || 
                prevProps.FinancialYearID !== FinancialYearID || 
                prevProps.QuarterID !== QuarterID) {
                this.checkAndFetch();
            }
            if (prevState.chartType !== this.state.chartType && this.lastResponse) {
                this.processAndRender(this.lastResponse);
            }
        }

        checkAndFetch = () => {
            const { reportName, FinancialYearID } = this.props;
            if (reportName && REPORT_CONFIG[reportName] && FinancialYearID !== 0) {
                this.fetchData();
            }
        }

        toggleType = (type) => { this.setState({ chartType: type }); }

        fetchData = () => {
            const { FinancialYearID, reportName, QuarterID } = this.props;
            const config = REPORT_CONFIG[reportName];
            this.setState({ loading: true, noData: false });

            const params = [
                { Name: "FinancialYearID", Value: FinancialYearID }, 
                { Name: "ExportToExcel", Value: "" }, 
                { Name: "Filter", Value: "" }
            ];
            
            if (config.hasQuarterFilter) {
                params.push({ Name: "QuarterID", Value: QuarterID || 0 });
            }

            utils.invokeUrl(config.apiUrl + "?request=" + utils.doGetRequestJson(0, 0, params, true), (response) => {
                if (!response || response.length === 0) {
                    this.setState({ loading: false, noData: true });
                    return;
                }
                this.lastResponse = response; 
                this.processAndRender(response);
            }, () => this.setState({ loading: false }));
        }

        processAndRender = (response) => {
            const { reportName, onDataLoaded } = this.props;
            const config = REPORT_CONFIG[reportName];
            const { chartType } = this.state;
            
            let categories = response.map(o => String(o.Values || o.Category || "Unknown"));
            let series = [];
            let totalCount = 0;

            // FIX: Bulletproof Line configurations directly injected into the series
            const isLine = chartType === 'line';
            const lineConfig = isLine ? {
                lineWidth: 3,
                marker: { enabled: true, radius: 4, symbol: 'circle' }
            } : {};

            if (chartType === 'pie') {
                series = [{
                    name: config.title,
                    type: 'pie',
                    data: response.map(o => {
                        const val = Math.abs(o[config.pieValueKey] || o.Total || 0);
                        totalCount += val;
                        return { name: String(o.Values || o.Category || "Unknown"), y: val };
                    })
                }];
            } else if (config.isMultiSeries) {
                
                // Add Total Indicators explicitly if it exists in the data
                if (response.length > 0 && response[0].TotalIndicators !== undefined) {
                    series.push({
                        name: 'Total Indicators', // Renamed to match the new SQL logic
                        color: '#9c27b0', // Purple
                        type: chartType,
                        ...lineConfig, 
                        data: response.map(o => o.TotalIndicators || 0)
                    });
                }

                series.push({ 
                    name: 'Target', 
                    color: '#00d4ff', // Cyan
                    type: chartType, 
                    ...lineConfig,
                    data: response.map(o => o[config.targetKey] || 0) 
                });
                
                series.push({ 
                    name: 'Actual', 
                    color: '#ffc107', // Yellow
                    type: chartType, 
                    ...lineConfig,
                    data: response.map(o => {
                        const v = o[config.achievementKey] || 0;
                        totalCount += v;
                        return v;
                    })
                });

                series.push({ 
                    name: config.performanceKey ? 'Perf %' : 'Variance', 
                    color: '#28a745', // Green
                    type: chartType, 
                    yAxis: 1, 
                    ...lineConfig,
                    tooltip: { valueSuffix: config.performanceKey ? ' %' : '' },
                    data: response.map(o => {
                        const val = o[config.performanceKey || config.varianceKey];
                        return val ? Number(val) : 0;
                    }) 
                });

            } else {
                series = [{ 
                    name: config.title, 
                    color: '#00d4ff', // Cyan for single series
                    type: chartType,
                    ...lineConfig,
                    data: response.map(o => {
                        const v = o.Total || 0;
                        totalCount += v;
                        return v;
                    }) 
                }];
            }

            this.setState({ loading: false }, () => {
                this.renderHighchart(categories, series, config);
            });
            if (onDataLoaded) onDataLoaded(totalCount, response);
        }

        renderHighchart(categories, series, config) {
            const containerId = "chart-container-" + this.props.reportName;
            if (this.chart) { this.chart.destroy(); }

            const { chartType } = this.state;
            const isPie = chartType === 'pie';

            this.chart = Highcharts.chart(containerId, {
                colors: ['#00d4ff', '#ffc107', '#28a745', '#9c27b0', '#ff6b6b', '#4ecdc4', '#ffe66d'],

                chart: { 
                    type: chartType, 
                    height: '400px', 
                    backgroundColor: 'transparent'
                },
                title: { text: ' ' },
                xAxis: {
                    categories: categories,
                    visible: !isPie,
                    labels: { 
                        style: { color: '#ccc', fontSize: '10px' },
                        rotation: chartType === 'column' ? -45 : 0 
                    }
                },
                yAxis: isPie ? null : [
                    { 
                        title: { text: '' }, 
                        gridLineColor: '#333',
                        min: 0,
                        labels: { style: { color: '#ccc' } }
                    },
                    { 
                        title: { text: '' }, 
                        opposite: true, 
                        visible: !!config.isMultiSeries, 
                        gridLineWidth: 0,
                        min: 0, 
                        labels: {
                            format: '{value}%',
                            style: { color: '#28a745' } 
                        }
                    }
                ],
                legend: { 
                    enabled: this.props.showLegend,
                    itemStyle: { color: '#E0E0E0' },
                    itemHoverStyle: { color: '#FFF' }
                },
                plotOptions: {
                    series: {
                        showInLegend: true,
                        cursor: 'pointer',
                        borderWidth: 0, // Cleans up columns
                        // FIX: Removed global line properties from here
                        point: {
                            events: {
                                click: (e) => {
                                    const val = isPie ? e.point.name : e.point.category;
                                    if(this.props.onPointClick) this.props.onPointClick(val);
                                }
                            }
                        }
                    },
                    pie: {
                        showInLegend: true,
                        allowPointSelect: true,
                        dataLabels: {
                            enabled: true,
                            connectorWidth: 1,
                            format: '<b>{point.name}</b>: {point.y}',
                            style: { textOutline: 'none', color: '#fff' }
                        }
                    },
                    column: {
                        pointPadding: 0.1,
                        groupPadding: 0.15 
                    },
                    bar: {
                        pointPadding: 0.1,
                        groupPadding: 0.15 
                    }
                },
                series: series,
                credits: { enabled: false }
            });
        }

        render() {
            const { loading, noData, chartType } = this.state;
            const isPie = defaultType === 'pie';
            const isLine = defaultType === 'line';
            
            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    {!isPie && !isLine && !loading && !noData && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '8px' }}>
                            <ButtonGroup className="chart-switcher-pills icon-switcher" variant="contained">
                                <Button 
                                    className={chartType === 'column' ? 'active' : ''} 
                                    onClick={(e) => { e.stopPropagation(); this.toggleType('column'); }}
                                    title="Vertical View"
                                >
                                    <BarChart style={{ fontSize: 20 }} />
                                </Button>
                                <Button 
                                    className={chartType === 'bar' ? 'active' : ''} 
                                    onClick={(e) => { e.stopPropagation(); this.toggleType('bar'); }}
                                    title="Horizontal View"
                                >
                                    <ViewWeek style={{ fontSize: 20, transform: 'rotate(90deg)' }} />
                                </Button>
                                <Button 
                                    className={chartType === 'line' ? 'active' : ''} 
                                    onClick={(e) => { e.stopPropagation(); this.toggleType('line'); }}
                                    title="Line View"
                                >
                                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>~</span>
                                </Button>
                            </ButtonGroup>
                        </div>
                    )}
                    
                    {loading && (
                        <div style={{ position: 'absolute', left: '50%', top: '45%', zIndex: 10 }}>
                            <CircularProgress size={30} />
                        </div>
                    )}

                    {noData && !loading && (
                        <div style={{ textAlign: 'center', paddingTop: '100px', color: '#888' }}>No data found.</div>
                    )}

                    <div id={"chart-container-" + this.props.reportName} style={{ width: '100%' }}>
                        <WrappedComponent {...this.props} chartRef={this.chartRef} />
                    </div>
                </div>
            );
        }
    }
}